import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Users, Heart, MessageCircle, Eye, Repeat2, TrendingUp, TrendingDown,
  BarChart3, MousePointerClick, AtSign, RefreshCw, Calendar, Download,
} from "lucide-react";
import { Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

const PLATFORM_ICONS: Record<string, React.ElementType> = {
  instagram: Instagram, twitter: Twitter, linkedin: Linkedin,
  facebook: Facebook, threads: AtSign,
};
const PLATFORM_COLORS: Record<string, string> = {
  instagram: "#e1306c", twitter: "#1da1f2", linkedin: "#0077b5",
  facebook: "#1877f2", threads: "#101010",
};

function formatK(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return String(n ?? 0);
}

function pct(val: number, prev: number) {
  if (!prev) return null;
  return ((val - prev) / prev) * 100;
}

export function SocialAnalyticsDashboard() {
  const { orgId } = useOrgPermissions();
  const { toast } = useToast();
  const [syncing, setSyncing] = useState(false);

  // Connected accounts
  const { data: accounts = [], refetch: refetchAccounts } = useQuery({
    queryKey: ["social-analytics-accounts", orgId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      let q = supabase.from("social_accounts")
        .select("id,platform,account_name,account_handle,follower_count,followers_count,following_count,posts_count,last_synced_at,token_expires_at,metadata,avatar_url")
        .eq("user_id", user.id).eq("is_connected", true);
      if (orgId) q = (q as any).eq("org_id", orgId);
      const { data } = await (q as any);
      return (data ?? []) as any[];
    },
  });

  // Follower growth (30 days)
  const { data: snapshots = [] } = useQuery({
    queryKey: ["follower-snapshots", accounts.map((a: any) => a.id)],
    enabled: accounts.length > 0,
    queryFn: async () => {
      const ids = accounts.map((a: any) => a.id);
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const { data } = await (supabase as any)
        .from("follower_snapshots")
        .select("social_account_id,snapshot_date,follower_count")
        .in("social_account_id", ids)
        .gte("snapshot_date", thirtyDaysAgo)
        .order("snapshot_date", { ascending: true });
      return (data ?? []) as any[];
    },
  });

  // Post analytics
  const { data: topPosts = [] } = useQuery({
    queryKey: ["top-posts-analytics", orgId],
    queryFn: async () => {
      let q = supabase.from("posts")
        .select("id,content,platform,status,published_at,social_account_id,post_analytics(likes_count,comments_count,shares_count,reach_count,impressions_count,clicks_count,engagement_rate,synced_at)")
        .eq("status", "published")
        .not("platform_post_id", "is", null)
        .order("published_at", { ascending: false })
        .limit(20);
      if (orgId) q = (q as any).eq("org_id", orgId);
      const { data } = await (q as any);
      return ((data ?? []) as any[])
        .filter((p: any) => p.post_analytics)
        .sort((a: any, b: any) => (b.post_analytics?.engagement_rate ?? 0) - (a.post_analytics?.engagement_rate ?? 0));
    },
  });

  // Aggregate stats
  const totalFollowers = accounts.reduce((s: number, a: any) => s + (a.follower_count ?? a.followers_count ?? 0), 0);
  const totalPosts = accounts.reduce((s: number, a: any) => s + (a.posts_count ?? 0), 0);

  const analyticsAccounts = topPosts.reduce((acc: Record<string, any>, p: any) => {
    const id = p.social_account_id;
    if (!acc[id]) acc[id] = { likes: 0, comments: 0, shares: 0, reach: 0, impressions: 0, clicks: 0, posts: 0 };
    const a = p.post_analytics;
    acc[id].likes       += a.likes_count ?? 0;
    acc[id].comments    += a.comments_count ?? 0;
    acc[id].shares      += a.shares_count ?? 0;
    acc[id].reach       += a.reach_count ?? 0;
    acc[id].impressions += a.impressions_count ?? 0;
    acc[id].clicks      += a.clicks_count ?? 0;
    acc[id].posts++;
    return acc;
  }, {});

  const analyticsValues = Object.values(analyticsAccounts) as any[];
  const totalLikes       = analyticsValues.reduce((s: number, a: any) => s + a.likes, 0);
  const totalComments    = analyticsValues.reduce((s: number, a: any) => s + a.comments, 0);
  const totalImpressions = analyticsValues.reduce((s: number, a: any) => s + a.impressions, 0);
  const totalReach       = analyticsValues.reduce((s: number, a: any) => s + a.reach, 0);

  // Follower growth chart data (aggregate across all accounts, last 30 days)
  const growthByDay: Record<string, number> = {};
  for (let i = 29; i >= 0; i--) {
    const d = new Date(Date.now() - i * 86400000).toISOString().slice(0, 10);
    growthByDay[d] = 0;
  }
  for (const snap of snapshots) {
    if (snap.snapshot_date in growthByDay) {
      growthByDay[snap.snapshot_date] += snap.follower_count ?? 0;
    }
  }
  const growthDays = Object.entries(growthByDay).map(([day, count]) => ({ day, count }));
  const maxGrowth = Math.max(1, ...growthDays.map(d => d.count));

  const exportCSV = () => {
    const rows = [
      ["Platform", "Handle", "Followers", "Following", "Posts", "Likes (30d)", "Comments (30d)", "Reach (30d)", "Impressions (30d)", "Clicks (30d)", "Last Synced"],
      ...accounts.map((a: any) => {
        const agg = analyticsAccounts[a.id] ?? {};
        return [
          a.platform, `@${a.account_handle}`,
          a.follower_count ?? a.followers_count ?? 0,
          a.following_count ?? 0, a.posts_count ?? 0,
          agg.likes ?? 0, agg.comments ?? 0, agg.reach ?? 0,
          agg.impressions ?? 0, agg.clicks ?? 0,
          a.last_synced_at ? new Date(a.last_synced_at).toISOString() : "",
        ];
      }),
    ];
    const csv = rows.map(r => r.map(v => `"${v}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `social-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      await Promise.allSettled(
        accounts.map((acc: any) =>
          supabase.functions.invoke(`sync-${acc.platform}-account`, {
            body: { socialAccountId: acc.id, accessToken: acc.access_token },
          })
        )
      );
      await supabase.functions.invoke("sync-analytics", { body: {} });
      await refetchAccounts();
      toast({ title: "Sync complete", description: "All platforms and post analytics updated." });
    } catch {
      toast({ title: "Sync failed", variant: "destructive" });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Social Analytics</h2>
          <p className="text-sm text-muted-foreground">Real data from all connected platforms</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV} disabled={accounts.length === 0}>
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleSyncAll} disabled={syncing}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync Now"}
          </Button>
        </div>
      </div>

      {/* Top KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Followers", value: formatK(totalFollowers), icon: Users, color: "text-purple-500", bg: "bg-purple-50" },
          { label: "Impressions (30d)", value: formatK(totalImpressions), icon: Eye, color: "text-blue-500", bg: "bg-blue-50" },
          { label: "Total Reach (30d)", value: formatK(totalReach), icon: TrendingUp, color: "text-emerald-500", bg: "bg-emerald-50" },
          { label: "Engagements (30d)", value: formatK(totalLikes + totalComments), icon: Heart, color: "text-rose-500", bg: "bg-rose-50" },
        ].map(stat => (
          <Card key={stat.label} className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
                <p className="text-2xl font-bold mt-1">{stat.value}</p>
              </div>
              <div className={`w-8 h-8 rounded-lg ${stat.bg} flex items-center justify-center`}>
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Per-platform account cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {accounts.map((acc: any) => {
          const Icon = PLATFORM_ICONS[acc.platform] ?? AtSign;
          const color = PLATFORM_COLORS[acc.platform] ?? "#666";
          const aggr = analyticsAccounts[acc.id] ?? {};
          const followers = acc.follower_count ?? acc.followers_count ?? 0;

          // Growth: compare first vs last snapshot for this account
          const acctSnaps = snapshots.filter((s: any) => s.social_account_id === acc.id);
          const firstSnap = acctSnaps[0]?.follower_count;
          const lastSnap  = acctSnaps[acctSnaps.length - 1]?.follower_count;
          const growth    = firstSnap && lastSnap ? lastSnap - firstSnap : null;
          const growthPct = firstSnap ? pct(lastSnap, firstSnap) : null;

          return (
            <Card key={acc.id} className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: color }}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold capitalize">{acc.platform}</p>
                  <p className="text-xs text-muted-foreground">@{acc.account_handle}</p>
                </div>
                {acc.last_synced_at && (
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    Synced {formatDistanceToNow(new Date(acc.last_synced_at), { addSuffix: true })}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-muted/40 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Followers</p>
                  <p className="text-lg font-bold">{formatK(followers)}</p>
                  {growthPct !== null && (
                    <p className={`text-[10px] font-medium flex items-center gap-0.5 mt-0.5 ${growthPct >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                      {growthPct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {growthPct >= 0 ? "+" : ""}{growthPct.toFixed(1)}% 30d
                    </p>
                  )}
                </div>
                <div className="bg-muted/40 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Following</p>
                  <p className="text-lg font-bold">{formatK(acc.following_count ?? 0)}</p>
                  {acc.posts_count > 0 && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">{formatK(acc.posts_count)} posts</p>
                  )}
                </div>
              </div>

              {Object.keys(aggr).length > 0 && (
                <div className="grid grid-cols-3 gap-2 text-center pt-3 border-t border-border">
                  {[
                    { label: "Likes", value: formatK(aggr.likes ?? 0), icon: Heart },
                    { label: "Comments", value: formatK(aggr.comments ?? 0), icon: MessageCircle },
                    { label: "Shares", value: formatK(aggr.shares ?? 0), icon: Repeat2 },
                  ].map(m => (
                    <div key={m.label}>
                      <m.icon className="w-3.5 h-3.5 text-muted-foreground mx-auto mb-0.5" />
                      <p className="text-sm font-semibold">{m.value}</p>
                      <p className="text-[10px] text-muted-foreground">{m.label}</p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Follower growth chart */}
      {growthDays.some(d => d.count > 0) && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-sm">Follower Growth</h3>
              <p className="text-xs text-muted-foreground">Combined across all platforms — last 30 days</p>
            </div>
          </div>
          <div className="flex items-end gap-0.5 h-24">
            {growthDays.map((d, i) => {
              const pctH = Math.max(2, (d.count / maxGrowth) * 100);
              const isToday = i === growthDays.length - 1;
              return (
                <div key={d.day} className="flex-1 group relative flex flex-col items-center justify-end" style={{ height: "88px" }}>
                  <div
                    className="w-full rounded-t-sm transition-all"
                    style={{ height: `${pctH}%`, background: isToday ? "#8b5cf6" : "#8b5cf620" }}
                  />
                  {i % 7 === 0 && (
                    <span className="absolute -bottom-5 text-[9px] text-muted-foreground">
                      {new Date(d.day).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Top performing posts */}
      {topPosts.length > 0 && (
        <Card className="p-5">
          <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            Top Performing Posts (by engagement rate)
          </h3>
          <div className="space-y-3">
            {topPosts.slice(0, 8).map((post: any, i: number) => {
              const a = post.post_analytics;
              const Icon = PLATFORM_ICONS[post.platform] ?? AtSign;
              const color = PLATFORM_COLORS[post.platform] ?? "#666";
              return (
                <div key={post.id} className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors">
                  <span className="text-xs font-bold text-muted-foreground w-4 shrink-0 mt-0.5">#{i + 1}</span>
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: color }}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm line-clamp-2 leading-snug">{post.content}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-[11px] text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" /> {formatK(a.likes_count ?? 0)}</span>
                      <span className="flex items-center gap-0.5"><MessageCircle className="w-3 h-3" /> {formatK(a.comments_count ?? 0)}</span>
                      <span className="flex items-center gap-0.5"><Repeat2 className="w-3 h-3" /> {formatK(a.shares_count ?? 0)}</span>
                      <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" /> {formatK(a.impressions_count ?? 0)} impressions</span>
                      {a.clicks_count > 0 && <span className="flex items-center gap-0.5"><MousePointerClick className="w-3 h-3" /> {formatK(a.clicks_count)}</span>}
                      {post.published_at && (
                        <span className="flex items-center gap-0.5 ml-auto">
                          <Calendar className="w-3 h-3" />
                          {formatDistanceToNow(new Date(post.published_at), { addSuffix: true })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-bold" style={{ color: a.engagement_rate > 3 ? "#16a34a" : a.engagement_rate > 1 ? "#ca8a04" : "#6b7280" }}>
                      {(a.engagement_rate ?? 0).toFixed(2)}%
                    </p>
                    <p className="text-[10px] text-muted-foreground">engagement</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {accounts.length === 0 && (
        <Card className="p-10 text-center">
          <BarChart3 className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
          <p className="font-medium text-muted-foreground">No connected accounts</p>
          <p className="text-sm text-muted-foreground mt-1">Connect social accounts to see analytics here.</p>
        </Card>
      )}
    </div>
  );
}
